import { useCallback, useEffect, useRef } from "react";
import { applyRangeSelection } from "@src/common/lib/rangeSelection";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";
import { useShipmentsCoreDataContext } from "../context/ShipmentsCoreData";

/**
 * Row checkbox toggling for the Orders table, with shift-click range
 * selection like the Routes stops list: shift-clicking a row sets every row
 * between it and the last clicked row to the clicked row's new state.
 *
 * The range runs over `rows` as rendered — the current page, after server
 * side sorting and filters.
 */
export function useShipmentCheckToggle() {
  const {
    checkedShipments,
    setCheckedShipments,
    isAllUnassignedChecked,
    setIsAllUnassignedChecked,
  } = useShipmentSelectionContext();
  const { rows } = useShipmentsCoreDataContext();
  // Anchored by id rather than index so it survives a refetch; if the row has
  // left the page the next shift-click is a plain toggle.
  const anchorIdRef = useRef<string>();
  // The selection this hook last set, to tell its own changes apart from
  // ones made elsewhere.
  const ownSelectionRef = useRef<string[]>();

  // A selection cleared elsewhere (date/filter change, select-all off,
  // finished bulk action) starts over, so the next shift-click can't extend
  // from a click made before the view changed. Unchecking the last row
  // yourself keeps the anchor.
  useEffect(() => {
    if (
      checkedShipments.length === 0 &&
      checkedShipments !== ownSelectionRef.current
    ) {
      anchorIdRef.current = undefined;
    }
  }, [checkedShipments]);

  return useCallback(
    (shipmentId: string, extendRange = false) => {
      const select = !checkedShipments.includes(shipmentId);
      const rowIds = rows.map((row) => row?.key).filter(Boolean) as string[];
      const anchorIndex = anchorIdRef.current
        ? rowIds.indexOf(anchorIdRef.current)
        : -1;
      const index = rowIds.indexOf(shipmentId);
      let nextChecked: string[];
      if (extendRange && anchorIndex !== -1 && index !== -1) {
        nextChecked = applyRangeSelection(
          checkedShipments,
          rowIds,
          anchorIndex,
          index,
          select
        );
      } else if (select) {
        nextChecked = [...checkedShipments, shipmentId];
      } else {
        nextChecked = checkedShipments.filter((id) => id !== shipmentId);
      }
      ownSelectionRef.current = nextChecked;
      setCheckedShipments(nextChecked);
      if (!select && isAllUnassignedChecked) {
        setIsAllUnassignedChecked(false);
      }
      anchorIdRef.current = shipmentId;
    },
    [
      checkedShipments,
      isAllUnassignedChecked,
      rows,
      setCheckedShipments,
      setIsAllUnassignedChecked,
    ]
  );
}
